import { ThunkDispatch } from "core-integration/src/store";
import { getFutureEvents } from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { updateH2HLeague } from "core-integration/src/store/leagues/thunks";
import {
  IPrivateLeagueAdmin,
  IUpdateH2HLeagueData,
  LeagueCodePrivacy,
} from "core-integration/src/store/leagues/types";
import range from "lodash/range";
import React, { useState } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import Button from "../../Button";
import {
  CheckboxField,
  FieldWrap,
  InputField,
  SelectField,
} from "../../FieldRenderers";
import SubHeading from "../../SubHeading";

interface IOwnProps {
  league: IPrivateLeagueAdmin;
  started: boolean;
}

type Props = IOwnProps & WithTranslation;

interface IState {
  codePrivacy: LeagueCodePrivacy;
  closed: boolean;
  koRounds: number;
  maxEntries: number;
  name: string;
  startEvent: number;
}

const H2HDetails = ({ league, started, t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const events = useSelector(getFutureEvents);
  const rules = useSelector(getRules);

  const requiredFields: Array<keyof IState> = [
    "codePrivacy",
    "name",
    "startEvent",
  ];

  const privacyOptions: { [key: string]: string } = {
    a: "All game players",
    l: "League members",
    p: "League administrator",
  };

  const [state, setState] = useState<IState>({
    closed: league.closed,
    codePrivacy: league.code_privacy,
    koRounds: league.ko_rounds || 0,
    maxEntries: league.max_entries || 0,
    name: league.name,
    startEvent: league.start_event,
  });

  const maximumKoRounds = () => {
    const remainingEvents = events.length;
    if (remainingEvents <= 1) {
      return 0;
    }
    return Math.min(
      rules.league_max_ko_rounds_private_h2h,
      remainingEvents - 1,
    );
  };

  const minimumSize = (koRounds: number) =>
    state.koRounds ? Math.pow(2, koRounds) : 2;

  const validStartingEvents = (koRounds: number) =>
    koRounds ? events.slice(0, -koRounds) : events;

  const apiDataFromState = (): IUpdateH2HLeagueData => ({
    admin_entry: league.admin_entry,
    closed: state.closed,
    code_privacy: state.codePrivacy,
    ko_rounds: state.koRounds || null,
    max_entries: state.maxEntries || null,
    name: state.name,
    start_event: state.startEvent,
  });

  const handleNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, name: e.currentTarget.value });

  const handleCodePrivacyChange = (
    e: React.SyntheticEvent<HTMLSelectElement>,
  ) =>
    setState({
      ...state,
      codePrivacy: e.currentTarget.value as LeagueCodePrivacy,
    });

  const handleStartEventChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, startEvent: parseInt(e.currentTarget.value, 10) });

  const handleClosedChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, closed: e.currentTarget.checked ? true : false });

  const handleMaxEntriesChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, maxEntries: parseInt(e.currentTarget.value, 10) });

  const handleKoRoundsChange = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    const koRounds = parseInt(e.currentTarget.value, 10);
    const minimumSizeKoRounds = minimumSize(koRounds);
    const events = validStartingEvents(koRounds);
    setState({
      ...state,
      koRounds,
      maxEntries: state.maxEntries
        ? Math.max(minimumSizeKoRounds, state.maxEntries)
        : 0,
      startEvent: Math.min(events[events.length - 1].id, state.startEvent),
    });
  };

  const updateLeague: (leagueId: number, data: IUpdateH2HLeagueData) => void = (
    leagueId,
    data,
  ) => dispatch(updateH2HLeague(leagueId, data));

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateLeague(league.id, apiDataFromState());
  };

  const disabled = requiredFields.some((e) => !state[e]);
  return (
    <>
      <SubHeading>{t("h2hDetails.details", "League details")}</SubHeading>
      <form onSubmit={handleSubmit}>
        <FieldWrap>
          <InputField
            id="ismLeagueName"
            label={t("h2hDetails.labels.name", "League name")}
            value={state.name}
            onChange={handleNameChange}
            hint={t("h2hDetails.hints.max", "Maximum 30 characters")}
            maxLength={30}
          />
        </FieldWrap>
        {!started && (
          <>
            <FieldWrap>
              <SelectField
                id="ismLeagueCodePrivacy"
                value={state.codePrivacy}
                onChange={handleCodePrivacyChange}
                label={t(
                  "h2hDetails.labels.leagueCodePrivacy",
                  "League code privacy",
                )}
                hint={t(
                  "h2hDetails.hints.viewableBy",
                  "This sets who the league code is viewable by",
                )}
              >
                {Object.keys(privacyOptions).map((p) => (
                  <option
                    value={p}
                    aria-selected={p === state.codePrivacy}
                    key={p}
                  >
                    {privacyOptions[p]}
                  </option>
                ))}
              </SelectField>
            </FieldWrap>
            <FieldWrap>
              <SelectField
                id="ismLeagueStartEvent"
                value={state.startEvent}
                onChange={handleStartEventChange}
                label={t("h2hDetails.labels.scoringStats", "Scoring starts")}
                hint={t(
                  "h2hDetails.hints.reflected",
                  "Any change will be reflected after the next points update",
                )}
              >
                {validStartingEvents(state.koRounds).map((e) => (
                  <option
                    value={`${e.id}`}
                    aria-selected={e.id === state.startEvent}
                    key={e.id}
                  >
                    {e.name}
                  </option>
                ))}
              </SelectField>
            </FieldWrap>
            <FieldWrap>
              <SelectField
                id="ismLeagueMaxSize"
                value={state.maxEntries}
                onChange={handleMaxEntriesChange}
                label={t("h2hDetails.labels.maxSize", "Maximum size")}
              >
                <option value={0} aria-selected={!state.maxEntries}>
                  {t("h2hDetails.labels.unlimited", "Unlimited")}
                </option>
                {range(
                  minimumSize(state.koRounds),
                  rules.league_max_size_private_h2h + 1,
                ).map((num) => (
                  <option
                    value={num}
                    aria-selected={num === state.maxEntries}
                    key={num}
                  >
                    {num}
                  </option>
                ))}
              </SelectField>
            </FieldWrap>
            {maximumKoRounds() > 0 && (
              <SelectField
                id="ismLeagueKoRounds"
                value={state.koRounds}
                onChange={handleKoRoundsChange}
                label={t("h2hDetails.labels.knockoutRounds", "Knockout rounds")}
              >
                <option value={0} aria-selected={!state.koRounds}>
                  {t("h2hDetails.noKORounds", "None")}
                </option>
                {range(1, maximumKoRounds() + 1).map((num) => (
                  <option
                    value={num}
                    aria-selected={num === state.koRounds}
                    key={num}
                  >
                    {num}
                  </option>
                ))}
              </SelectField>
            )}
            <Box mt={2}>
              <CheckboxField
                checked={state.closed}
                id="ismLeagueClosed"
                label={t(
                  "h2hDetails.labels.closed",
                  "Close league to new entries",
                )}
                onChange={handleClosedChange}
              />
            </Box>
          </>
        )}
        <Box mt={2}>
          <Button type="submit" disabled={disabled}>
            {t("h2hDetails.labels.updateLeague", "Update league")}
          </Button>
        </Box>
      </form>
    </>
  );
};

export default withTranslation()(H2HDetails);
